/* global firebase */
import { useState, useEffect } from 'react';

const db = firebase.firestore();


/**
 * A react hook to load all companies in the taptivate platform. 
 */
export function useCompanies() {
  const [companies, setCompanies] = useState(null);
  
  useEffect(() => {
    db
      .collection('companies')
      .get()
      .then(snap => {
        const all = [];
        snap.forEach(doc => 
          all.push({
            id: doc.id,
            ...doc.data()
          })
        );
        setCompanies(all);
      });
  }, []);
  
  return companies;
}


/**
 * Use a company subscription
 * @param {String} companyId  The company id.
 */
export function useSubscription(companyId) {
  const [subscription, setSubscription] = useState(null);
  
  useEffect(() => {
    if(!companyId) {
      setSubscription(null);
      return;
    }
    firebase.functions().httpsCallable('getSubscription')(companyId)
      .then(res => {
        setSubscription(res.data);
      })
      .catch(error => {
        console.error(error);
        setSubscription(null);
      });
  }, [companyId]);
  
  return subscription;
}


/**
 * Use a company invoices
 * @param {String} companyId  The company id.
 */
export function useInvoices(companyId) {
  const [invoices, setInvoices] = useState(null);
  
  useEffect(() => {
    if(!companyId) {
      setInvoices(null);
      return;
    }
    firebase.functions().httpsCallable('getInvoices')(companyId)
      .then(res => {
        setInvoices(res.data);
      })
      .catch(error => {
        console.error(error);
        setInvoices(null);
      });
  }, [companyId]);
  
  return invoices;
}


/**
 * A react hook to subscribe to an individual company data.
 * @param {String} companyId
 */
export function useCompany(companyId) {
  const [company, setCompany] = useState(null);
  
  useEffect(() => {
    
    // todo: check permissions
    
    if(companyId) {
      return db
        .collection('companies')
        .doc(companyId)
        .onSnapshot(doc => {
          if(!doc.exists) {
            return;
          }
          setCompany({
            id: companyId,
            ...doc.data(),
          });
        });
    }
    else {
      setCompany(null);
    }
    
  }, [companyId]);
  
  return company;
}


/**
 * Create a new company.
 * 
 * @param {Object} company
 * @param {String} company.name
 * @param {String} company.street
 * @param {String} company.city
 * @param {String} company.state
 * @param {String} company.zip
 */
export async function createCompany(company={}) {
  
  // todo: check permissions? 
  // note: this is used by auth to sign up a new user.
  
  // Create the company.
  
  const ref = await db
    .collection('companies')
    .add(company);
  
  // NO DEFAULT TEMPLATES!
  // Add default templates to the new company.
  // for(const template of defaultTemplates) {
  //   ref.collection('templates').add({
  //     ...template,
  //     dateCreated: Date.now(),
  //   });
  // }
  
  return {
    id: ref.id,
  };
}


/**
 * Update the logged in user's company info. 
 * 
 * @param {String} companyId
 * 
 * @param {Object} company
 * @param {String} company.name
 * @param {String} company.street
 * @param {String} company.city
 * @param {String} company.state
 * @param {String} company.zip
 */
export async function updateCompany(companyId, company={}) {
  
  // todo: check permissions
  
  await db
    .collection('companies')
    .doc(companyId)
    .update(company);
  
}


/**
 * Add a user to a company.
 * 
 * @param {String} userId
 * @param {String} companyId
 */
export async function addUserToCompany(userId, companyId) {
  
  // todo: check permissions
  // todo: check that company isn't over number of seats
  
  const user = await db 
    .collection('users')
    .doc(userId)
    .get()
    .then(doc => doc.data());
  
  await db
    .collection('companies').doc(companyId)
    .collection('users').doc(userId).set({
      displayName: user.displayName,
      email: user.email,
      role: user.role || "CONTRIBUTOR",
    });
    
  await db
    .collection('users').doc(userId)
    .update({
      companyId: companyId
    });
    
}


/** 
 * Remove a user from a company.
 * 
 * @param {String} userId
 * @param {String} companyId
 */
export async function removeUserFromCompany(userId, companyId) {
  
  // todo: check permissions
  
  await db
    .collection('companies').doc(companyId)
    .collection('users').doc(userId)
    .delete();
    
  await db
    .collection('users').doc(userId)
    .update({
      companyId: null,
    });
    
}


/**
 * Create a company subscription.
 * 
 * @async
 * @param {Object} req
 * @param {String} companyId  The company id.
 * @param {String} token      Token generated by stripe.
 * @param {String} planId     The ID of the chosen plan.
 */
export const createSubscription = firebase.functions().httpsCallable('createSubscription');


/**
 * Change a company subscription.
 * @param {Object} req
 * @param {String} companyId  The company id.
 * @param {String} planId     The ID of the chosen plan.
 */
export const changeSubscription = firebase.functions().httpsCallable('changeSubscription');


/**
 * Activate subscription.
 * @param {String} companyId  The company id.
 */
export const activateSubscription = firebase.functions().httpsCallable('activateSubscription');


/**
 * Cancel a subscription.
 * @param {String} companyId
 */
export const cancelSubscription = firebase.functions().httpsCallable('cancelSubscription');


/**
 * Update company payment method.
 */
export const updatePayment = firebase.functions().httpsCallable('updatePayment');
